/**
 * color-description.js
 * ---------------------------------------------------------------------------
 * Purpose (read this before touching anything):
 *
 * On the product page, when a visitor clicks a color swatch, this swaps the
 * "Description" text (whether it's shown as a plain text block or inside the
 * collapsible accordion row) for a color-specific description, when one has
 * been filled in for that color via the "custom.descripcion_color" metafield
 * on the matching variant(s). If no color-specific description was filled in
 * for the clicked color, it falls back to the product's normal description
 * (the one that was already on the page before any click).
 *
 * How it finds things on the page (rendered by the theme — see
 * snippets/product-block-variant-picker.liquid and
 * snippets/product-block-description.liquid):
 *   - Each color-swatch group is wrapped in an element carrying
 *     [data-color-swatches-group="<product id>"].
 *   - Right next to that group, a <script type="application/json"
 *     id="color-description-map-<product id>"> holds a JSON object mapping
 *     each color value to its description HTML (or "" when none was set for
 *     that color).
 *   - The actual description container (the accordion row's content, or the
 *     plain text block) carries [data-color-description-target="<product id>"].
 *
 * This script does not know or care whether the description is shown as an
 * accordion row or a plain text block — it just swaps innerHTML on whatever
 * carries data-color-description-target for that product.
 *
 * This is purely a front-end swap (like currency-converter.js): nothing here
 * touches checkout, inventory, or which variant actually gets added to cart.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var originalDescriptions = new WeakMap();
  var descriptionMaps = {}; // productId -> parsed map (cached so we don't re-parse on every click)
  var initializedGroups = new WeakSet();

  function getDescriptionMap(productId) {
    if (Object.prototype.hasOwnProperty.call(descriptionMaps, productId)) {
      return descriptionMaps[productId];
    }
    var mapScript = document.getElementById('color-description-map-' + productId);
    var map = null;
    if (mapScript) {
      try {
        map = JSON.parse(mapScript.textContent);
      } catch (e) {
        map = null;
      }
    }
    descriptionMaps[productId] = map;
    return map;
  }

  function getTargets(productId) {
    return document.querySelectorAll('[data-color-description-target="' + productId + '"]');
  }

  function applyColorDescription(productId, colorValue) {
    var map = getDescriptionMap(productId);
    var targets = getTargets(productId);

    targets.forEach(function (target) {
      if (!originalDescriptions.has(target)) {
        originalDescriptions.set(target, target.innerHTML);
      }

      var override = map ? map[colorValue] : '';
      if (override) {
        target.innerHTML = override;
      } else {
        target.innerHTML = originalDescriptions.get(target);
      }
    });
  }

  function initGroup(group) {
    if (initializedGroups.has(group)) return;
    initializedGroups.add(group);

    var productId = group.getAttribute('data-color-swatches-group');
    if (!productId) return;

    // If a color is already selected on load (e.g. arrived via a variant
    // link), apply its description immediately instead of waiting for a click.
    var selectedButton = group.querySelector('[data-button][data-option-value].selected');
    if (selectedButton) {
      applyColorDescription(productId, selectedButton.getAttribute('data-option-value'));
    }
  }

  function initExistingGroups() {
    document.querySelectorAll('[data-color-swatches-group]').forEach(initGroup);
  }

  function bindClicks() {
    document.body.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-button][data-option-value]');
      if (!button) return;

      // Only react to clicks on color swatches, not size chips or other
      // data-button options — those don't live inside a color-swatches group.
      var group = button.closest('[data-color-swatches-group]');
      if (!group) return;

      var productId = group.getAttribute('data-color-swatches-group');
      applyColorDescription(productId, button.getAttribute('data-option-value'));
    });
  }

  function observeDom() {
    // Handles quick-view modals / any markup injected after first load.
    var observer = new MutationObserver(function (mutations) {
      var hasAddedNodes = mutations.some(function (m) {
        return m.addedNodes && m.addedNodes.length > 0;
      });
      if (hasAddedNodes) initExistingGroups();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    initExistingGroups();
    bindClicks();
    observeDom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
